import * as cfe from 'ego-cfe';
import * as api from 'ego-sdk-js';
import { m } from 'motion/react';
import React, { useEffect } from 'react';

import * as Config from '../config';

import useApiClient, { useNoAuthApiClient } from './hooks/useApiClient';
import useUserMeInternal from './hooks/useUserMeInternal';
import CircleDownIcon from './icon/CircleDown';
import Alert from './lib/Alert';
import Collapse from './lib/Collapse';
import Pagelet from './Pagelet';

const InstallExtensionPage = () => {
  const accountInfo = useUserMeInternal();
  const apiClient = useApiClient();
  const noAuthApiClient = useNoAuthApiClient(true);

  const { result: chromeExtensionInfoRes } = cfe.ApiHook.useApiRead(
    noAuthApiClient,
    noAuthApiClient.accountGetChromeExtensionInfo,
    undefined,
    res => res,
  );

  useEffect(() => {
    // This is janky because this page is polling whether the extension is
    // open. Ideally, the extension would send a request to the page if it
    // happens to be open on the login page.
    function extensionPoller() {
      if (!accountInfo) {
        return;
      }
      if (!cfe.ApiData.hasData(chromeExtensionInfoRes)) {
        return;
      }
      const chromeExtensionId = chromeExtensionInfoRes.data.chrome_extension_id;
      chrome.runtime.sendMessage(chromeExtensionId, { type: 'ping', username: accountInfo.username }, response => {
        if (response === undefined || response.type !== 'request_token') {
          return;
        }
        apiClient.accountTokenFromWebSession().then(resp => {
          if (resp.kind === api.StatusCode.Ok) {
            chrome.runtime.sendMessage(
              chromeExtensionId,
              { type: 'grant_auth_token', auth_token: resp.result.token },
              _ => {
                // No-op.
              },
            );
          }
        });
      });
    }
    const intervalId = setInterval(extensionPoller, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [chromeExtensionInfoRes]);

  const [showPrivacyNotice, setShowPrivacyNotice] = React.useState(false);

  return (
    <Pagelet title="Get Chrome Extension" noBgPrimary gutter>
      <Pagelet.Heading1>Get Chrome Extension</Pagelet.Heading1>
      <div className="tw-mt-4"></div>
      <p>
        The Chrome extension is an easy way to use Superego from your browser on any site. With it installed, you can
        bring up Superego on any page to <b>give ink</b>, <b>share it</b>, <b>save it</b>, <b>find discussions</b>, or{' '}
        <b>follow the author</b>.
      </p>
      <div className="tw-cursor-pointer" role="button" onClick={() => setShowPrivacyNotice(!showPrivacyNotice)}>
        <Alert variant="info">
          <Alert.Heading>
            <div className="tw-flex tw-justify-between">
              <span>This extension respects your privacy. Click to learn more.</span>
              <m.div initial={{ rotateZ: 0 }} animate={{ rotateZ: showPrivacyNotice ? 180 : 0 }}>
                <CircleDownIcon size="1.5rem" />
              </m.div>
            </div>
          </Alert.Heading>
          <Collapse open={showPrivacyNotice}>
            <div className="tw-my-2">
              Our extension does not have access to your browsing history. It only has access to the site you're
              visiting when you open the extension. You can verify when "Adding to Chrome" that the only permission is
              "Communicate with cooperating websites". The only "cooperating website" is our own which we use for
              one-click sign in.
            </div>
          </Collapse>
        </Alert>
      </div>

      <Pagelet.Heading3 className="tw-mt-6">Step 1: Install from the Chrome Web Store</Pagelet.Heading3>
      <p>
        <a href="https://chrome.google.com/webstore/detail/intertimes/fkdlaeecchgindhnapnndndidcacielp" target="_blank">
          Click here to go to the store.
        </a>{' '}
        Click the "Add to Chrome" button.
      </p>
      <img
        className="tw-max-w-96 tw-shadow tw-rounded-lg"
        src={Config.globalStaticHost + 'mind/img/chrome-ext-app-store.png'}
      />

      <Pagelet.Heading3 className="tw-mt-6">Step 2: Verify</Pagelet.Heading3>
      <p>You should now have a Superego icon next to the address bar.</p>
      <img
        className="tw-max-w-96 tw-shadow tw-rounded-lg"
        src={Config.globalStaticHost + 'mind/img/chrome-ext-spot.png'}
      />
      <p className="tw-mt-6">If you don't see it, click the white puzzle icon to see all of your extensions.</p>

      <Pagelet.Heading3 className="tw-mt-6">Step 3: Sign in</Pagelet.Heading3>
      <img
        className="tw-max-w-48 tw-shadow tw-rounded-lg"
        src={Config.globalStaticHost + 'mind/img/chrome-ext-sign-in.png'}
      />

      <Pagelet.Heading3 className="tw-mt-6">Step 4: Go make the Internet a better place</Pagelet.Heading3>
      <img
        className="tw-max-w-72 tw-shadow tw-rounded-lg"
        src={Config.globalStaticHost + 'mind/img/chrome-ext-main-page.png'}
      />

      <div className="tw-mt-6" />
    </Pagelet>
  );
};

export default InstallExtensionPage;
